import { mapboxToken } from "../map/map.selector";
import { validStateFips, validStateAbbrs } from '../constants/locationInfo';

const getMapColorRange = (state, county, isClimate) => {
  state = Object.values(state);
  county = Object.values(county);

  if (!state.length) state = [0];
  if (!county.length) county = [0];

  let countyMax = Math.max(...county);
  let countyMin = Math.min(...county);
  let stateMax = Math.max(...state);
  let stateMin = Math.min(...state);

  if (isClimate) {
    stateMax = Math.max(Math.abs(stateMax), Math.abs(stateMin)).toFixed(2);
    stateMin = -stateMax;
    countyMax = Math.max(Math.abs(countyMax), Math.abs(countyMin)).toFixed(2);
    countyMin = -countyMax;
  } else {
    stateMax = Math.ceil(stateMax / 10) * 10;
    stateMin = 0;
    countyMax = Math.ceil(countyMax / 10) * 10;
    countyMin = 0;
  }

  return {
    state: {
      min: stateMin,
      max: stateMax,
    },
    county: {
      min: countyMin,
      max: countyMax,
    },
  };
};

const calcColor = (cbFriendly, isReversed, isPrecip, trend, min, max) => {
  let color = 'hsla(0, 100%, 100%, 0.95)';

  if (isReversed) {
    // Reverses color scheme for when higher numbers mean more cold
    if (trend > 0) {
      color = `hsla(210, 100%, ${90 - (trend / max) * 65}%, 0.95)`;
    } else if (trend < 0) {
      color = `hsla(0, 100%, ${90 - (trend / min) * 65}%, 0.95)`;
    }
  } else {
    if (trend > 0) {
      if (isPrecip) {
        color = cbFriendly
          ? `hsla(103, 63.8%, ${100 - (trend / max) * 58.8}%, 0.95)`
          : `hsla(135, 100%, ${75 - (trend / max) * 75}%, 0.95)`;
      } else {
        color = `hsla(0, 100%, ${90 - (trend / max) * 65}%, 0.95)`;
      }
    } else if (trend < 0) {
      if (isPrecip) {
        color = cbFriendly
          ? `hsla(323, 76.3%, ${100 - (trend / min) * 53.6}%, 0.95)`
          : `hsla(40, 100%, ${75 - (trend / min) * 75}%, 0.95)`;
      } else {
        color = `hsla(210, 100%, ${90 - (trend / min) * 65}%, 0.95)`;
      }
    }
  }

  return color;
};

const generateClimateExpressions = (
  cbFriendly,
  data,
  isReversed,
  isPrecip,
  { min, max },
  isState
) => {
  let expArr = isState
    ? ['match', ['get', 'STUSPS']]
    : ['match', ['get', 'GEOID']];

  Object.entries(data).forEach(([location, trend]) => {
    expArr.push(
      location,
      calcColor(cbFriendly, isReversed, isPrecip, trend, min, max)
    );
  });

  expArr.push('rgba(0,0,0,0)');
  return expArr;
};

export const getClimateColors = (
  cbFriendly,
  trendsObj,
  isPrecip,
  isReversed
) => {
  // Filter to relevant states and counties so that analyses and colors are properly calculated
  const filteredStateData = Object.fromEntries(
    Object.entries(trendsObj.state).filter(([stateAbbr, value]) => validStateAbbrs.includes(stateAbbr))
  );
  const filteredCountyData = Object.fromEntries(
    Object.entries(trendsObj.county).filter(([countyFips, value]) => validStateFips.includes(countyFips.slice(0,2)))
  );

  const range = getMapColorRange(filteredStateData, filteredCountyData, true);
  const stateExpression = generateClimateExpressions(
    cbFriendly,
    filteredStateData,
    isReversed,
    isPrecip,
    range.state,
    true
  );
  const countyExpression = generateClimateExpressions(
    cbFriendly,
    filteredCountyData,
    isReversed,
    isPrecip,
    range.county,
    false
  );

  return {
    mapColorRange: range,
    stateColors: stateExpression,
    countyColors: countyExpression,
  };
};

const pickColor = (value) => {
  let h, s, l;
  if (value === 0) {
    [h, s, l] = [40, 0, 50];
  } else if (value < 1e3) {
    [h, s, l] = [37.85, 80.25, 84.12];
  } else if (value < 1e4) {
    [h, s, l] = [39.13, 86.47, 73.92];
  } else if (value < 1e5) {
    [h, s, l] = [39.3, 92.43, 63.73];
  } else if (value < 1e6) {
    [h, s, l] = [39.83, 99.16, 53.53];
  } else if (value < 1e7) {
    [h, s, l] = [30, 100, 50];
  } else if (value < 1e8) {
    [h, s, l] = [20, 100, 50];
  } else if (value < 1e9) {
    [h, s, l] = [10, 100, 50];
  } else {
    [h, s, l] = [0, 100, 50];
  }

  return `hsla(${h}, ${s}%, ${l}%, 0.95)`;
};

const generateHazardExpressions = (data, isState, isDamage, max) => {
  let expArr = isState
    ? ['match', ['get', 'STUSPS']]
    : ['match', ['get', 'GEOID']];

  if (data) {
    if (isDamage) {
      Object.entries(data).forEach(([location, value]) => {
        expArr.push(location, pickColor(value));
      });
    } else {
      Object.entries(data).forEach(([location, value]) => {
        let color =
          max === 0
            ? 'hsla(40,0%,50%,0.95)'
            : `hsla(40, ${(value / max) * 100}%, 50%, 0.95)`;
        expArr.push(location, color);
      });
    }
  }

  expArr.push('hsla(40,0%,50%,0.95)');
  return expArr;
};

export const getHazardColors = (stateData, countyData, isDamage) => {
  if (stateData && countyData) {
    // Filter to relevant states and counties so that analyses and colors are properly calculated
    const filteredStateData = Object.fromEntries(
      Object.entries(stateData).filter(([stateAbbr, value]) => validStateAbbrs.includes(stateAbbr))
    );
    const filteredCountyData = Object.fromEntries(
      Object.entries(countyData).filter(([countyFips, value]) => validStateFips.includes(countyFips.slice(0,2)))
    );
    
    const range = getMapColorRange(filteredStateData, filteredCountyData, false);
    const stateExpression = generateHazardExpressions(
      filteredStateData,
      true,
      isDamage,
      range.state.max
    );
    const countyExpression = generateHazardExpressions(
      filteredCountyData,
      false,
      isDamage,
      range.county.max
    );

    return {
      mapColorRange: range,
      stateColors: stateExpression,
      countyColors: countyExpression,
    };
  } else {
    return {
      mapColorRange: null,
      stateColors: null,
      countyColors: null,
    };
  }
};


const bbox = [-98.85, 31.65, -71.13, 49.82];
export const search = async (terms) => {
  let results = await fetch(
    `https://api.mapbox.com/search/geocode/v6/forward?access_token=${mapboxToken}&country=US&bbox=${bbox.join(',')}&q=${terms.trim()}`
  );

  if (!results.ok) {
    throw new Error('Error');
  }

  results = await results.json();
  let location = results.features[0];

  let name, lat, lng;
  if (location) {
    name = location.properties.full_address.replace(', United States','');
    lat = location.properties.coordinates.latitude;
    lng = location.properties.coordinates.longitude;
  }

  return {
    lat,
    lng,
    name: name || `No results for: ${terms.trim()}`,
  };
};
